import {
  type GetByShortIDAssetResponse,
  type GetByShortIDBoardResponse,
  type GetByShortIDClipResponse,
  type GetByShortIDCollectionResponse,
  type GetByShortIdResponse,
  Permissions,
  type PublicClip,
  type ScopedPermissions,
  ShortUrl,
} from '@air/api';
import { type Board, type Clip, ShortUrlObjectType, type ShortUrlResponse } from '@air/api/types';

import { I18n } from '~/constants/I18n';

export const getPublicClipShareUrl = (clipShortId: string) => `${window.location.origin}/a/${clipShortId}`;

export const getPublicClipInABoardShareUrl = (boardShortId: string, clipId: Clip['id'], boardId: Board['id']) =>
  `${window.location.origin}/a/${boardShortId}/b/${boardId}/c/${clipId}`;

export const getPublicClipInAShowcaseShareUrl = (showcaseShortId: string, clipId: Clip['id']) =>
  `${window.location.origin}/a/${showcaseShortId}/c/${clipId}`;

export const getPublicBoardShareUrl = (shortId: string) => `${window.location.origin}/a/${shortId}`;

export const getPublicSubboardShareUrl = (parentShortId: string, boardId: string) =>
  `${window.location.origin}/a/${parentShortId}/b/${boardId}`;

export const getSeoTitle = (fallback: string, ...args: (string | undefined | null)[]) =>
  args.filter(Boolean).join(' / ') || fallback;

export const getShareSeoDescription = (description?: string | null) => description || I18n.shareLinkFallbackDescription;

export type ClipSeoData = {
  id: string;
  title: string | null;
  description: string | null;
  assets: { image: string | null };
  width: number;
  height: number;
};

export const convertClipToSeoData = (
  clip: Pick<Clip, 'id' | 'title' | 'description' | 'assets' | 'width' | 'height'>,
) => ({
  id: clip.id,
  title: clip.title ?? null,
  description: clip.description ?? null,
  assets: { image: clip.assets.image },
  width: clip.width,
  height: clip.height,
});

/**
 * Everything a BOARD share link (`/a/b…/b/{boardId}`) renders from — the route's own object, fetched
 * fully at SSR time (and again by `onVerified` after a passcode unlock).
 */
export type PublicBoardShareData = {
  board: GetByShortIDBoardResponse['data']['board'];
  workspace: GetByShortIDBoardResponse['data']['workspace'];
  permissions: ScopedPermissions;
  presentationSettings?: ShortUrlResponse['presentationSettings'];
  requireAccount: boolean;
  isOnPaidPlan: boolean | undefined;
};

/**
 * Everything a COLLECTION share link (`/a/m…`) renders from — the route's own object. The
 * `presentation*` fields ride along from the same `getByShortId` call so a board deep-link into the
 * collection still has the sub-page nav + content chrome available at SSR time.
 */
export type PublicShowcaseShareData = {
  collection: GetByShortIDCollectionResponse['data'];
  permissions: ScopedPermissions;
  presentationSettings?: ShortUrlResponse['presentationSettings'];
  presentationContent?: ShortUrlResponse['presentationContent'];
  presentationPages?: ShortUrlResponse['presentationPages'];
  requireAccount: boolean;
  isOnPaidPlan: boolean | undefined;
};

export type PublicClipShareObjectType = ShortUrlObjectType.clip | ShortUrlObjectType.asset;

type PublicClipShareObjectIdParams = {
  objectType: PublicClipShareObjectType;
  clip: Pick<PublicClip, 'id' | 'assetId'>;
};

/**
 * ShortUrl row identity for a clip/asset share page. Asset links store the asset id;
 * clip links store the clip id. The resolved version on screen is always `clip.id`.
 */
export const getPublicClipShareObjectId = ({ objectType, clip }: PublicClipShareObjectIdParams) =>
  objectType === ShortUrlObjectType.asset ? clip.assetId : clip.id;

/** Everything a CLIP or ASSET share link (`/a/c…`, `/a/a…`) renders from — the route's own object. */
export type PublicClipShareData = {
  clip: PublicClip;
  /** Wire `objectType` from getByShortId — the short-id prefix (`c`/`a`) is assigned at creation and does not update if the link is later converted clip ↔ asset. */
  objectType: PublicClipShareObjectType;
  /** Matches `objectType`: asset id for asset links, clip id for clip links. */
  objectId: string;
  permissions: ScopedPermissions;
  presentationSettings?: ShortUrlResponse['presentationSettings'];
  requireAccount: boolean;
  isOnPaidPlan: boolean | undefined;
};

/** The board-typed member of `getByShortId`'s response union, with the response-level extras. */
export type PublicBoardShareResponse = GetByShortIdResponse & GetByShortIDBoardResponse;

/** The clip/asset-typed members of `getByShortId`'s response union, with the response-level extras. */
export type PublicClipShareResponse = GetByShortIdResponse & (GetByShortIDAssetResponse | GetByShortIDClipResponse);

/** The collection-typed member of `getByShortId`'s response union, with the response-level extras. */
export type PublicShowcaseShareResponse = GetByShortIdResponse & GetByShortIDCollectionResponse;

/**
 * Completes a board share link's data from an already-resolved `getByShortId` response: the
 * descendant board record (`:boardId` in `/a/:shortId/b/:boardId` can be the root board or any
 * sub-board) and the board-scoped permissions, fetched in parallel — the passcode gate has already
 * passed by the time this runs, because `getByShortId` succeeded.
 */
export const fetchPublicBoardShareData = async ({
  shortId,
  boardId,
  response,
}: {
  shortId: string;
  boardId: string;
  response: PublicBoardShareResponse;
}): Promise<PublicBoardShareData> => {
  const [descendantBoardResponse, permissions] = await Promise.all([
    ShortUrl.getDescendantBoard(shortId, boardId),
    Permissions.getFromObjects({ shortId, objects: { boardIds: [boardId] } }),
  ]);

  return {
    // Long-standing merge, preserved as-is: the link's root-board record wins over the descendant's
    // for the keys it carries at runtime; the descendant contributes the rest (e.g. `ancestors`).
    board: {
      ...descendantBoardResponse.board,
      ...response.data.board,
    },
    workspace: response.data.workspace,
    permissions,
    ...(response.presentationSettings ? { presentationSettings: response.presentationSettings } : {}),
    requireAccount: response.requireAccount,
    isOnPaidPlan: response.isOnPaidPlan,
  };
};

/**
 * Completes a clip/asset share link's data from an already-resolved `getByShortId` response: the
 * clip-scoped permissions plus the packaging. Shared by the route's getStaticProps (ISR) and its
 * onVerified (post-passcode) — the duplication is structural, not accidental: a passcode-protected
 * link's data can only ever be fetched client-side, because ISR renders with no request context and
 * its cache is shared across visitors (the server never sees a visitor's passcode token).
 */
export const fetchPublicClipShareData = async ({
  shortId,
  response,
}: {
  shortId: string;
  response: PublicClipShareResponse;
}): Promise<PublicClipShareData> => {
  const permissions = await Permissions.getFromObjects({ shortId, objects: { clipIds: [response.data.id] } });

  return {
    clip: response.data,
    objectType: response.type,
    objectId: getPublicClipShareObjectId({ objectType: response.type, clip: response.data }),
    permissions,
    ...(response.presentationSettings ? { presentationSettings: response.presentationSettings } : {}),
    requireAccount: response.requireAccount,
    isOnPaidPlan: response.isOnPaidPlan,
  };
};

/**
 * Completes a collection share link's data from an already-resolved `getByShortId` response: the
 * collection-scoped permissions plus the packaging (including the presentation* fields the
 * collection tree renders from). Shared by getStaticProps (ISR) and onVerified (post-passcode) —
 * see fetchPublicClipShareData for why both call sites exist.
 */
export const fetchPublicShowcaseShareData = async ({
  shortId,
  response,
}: {
  shortId: string;
  response: PublicShowcaseShareResponse;
}): Promise<PublicShowcaseShareData> => {
  const permissions = await Permissions.getFromObjects({ shortId, objects: { collectionIds: [response.data.id] } });

  return {
    collection: response.data,
    permissions,
    ...(response.presentationSettings ? { presentationSettings: response.presentationSettings } : {}),
    ...(response.presentationContent ? { presentationContent: response.presentationContent } : {}),
    ...(response.presentationPages ? { presentationPages: response.presentationPages } : {}),
    requireAccount: response.requireAccount,
    isOnPaidPlan: response.isOnPaidPlan,
  };
};
